import { CHART_LABEL_FORMATS } from './types';
import type {
  BarChartCategoryAxis,
  BarChartSeries,
  ChartLabelFormat,
  NormalizedBarChartData,
} from './types';

import { CHART_MAX_SERIES } from './chart-tokens';
import { isFiniteNumber, isNonEmptyString } from './chart-helpers';

/** Loose runtime input shapes for untrusted upstream data. */
export interface RawBarChartCategoryAxisInput {
  label?: unknown;
  categories?: unknown;
}

export interface RawBarChartSeriesInput {
  label?: unknown;
  values?: unknown;
}

export interface BarChartValidationInput {
  categoryAxis?: RawBarChartCategoryAxisInput | null;
  series?: ReadonlyArray<RawBarChartSeriesInput | null | undefined> | null;
  format?: unknown;
}

export type BarChartValidationResult =
  | { valid: true; normalized: NormalizedBarChartData }
  | { valid: false; reason: string };

const DEFAULT_LABEL_FORMAT: ChartLabelFormat = 'number';

const isChartLabelFormat = (value: unknown): value is ChartLabelFormat =>
  typeof value === 'string' &&
  (CHART_LABEL_FORMATS as readonly string[]).includes(value);

const warn = (message: string): void => {
  console.warn(`[BarChart] ${message}`);
};

/** Stringifies without throwing on BigInt or circular structures. */
const safeStringify = (value: unknown): string => {
  try {
    const serialized = JSON.stringify(value);

    return serialized ?? String(value);
  } catch {
    return String(value);
  }
};

const validateCategoryAxisShape = (
  categoryAxis: RawBarChartCategoryAxisInput | null | undefined
): string | null => {
  if (
    !categoryAxis ||
    !Array.isArray(categoryAxis.categories) ||
    categoryAxis.categories.length === 0
  ) {
    return 'categoryAxis.categories is missing or empty';
  }

  for (let i = 0; i < categoryAxis.categories.length; i++) {
    if (typeof categoryAxis.categories[i] !== 'string') {
      return `categoryAxis.categories[${i}] is not a string`;
    }
  }

  return null;
};

const validateSeriesShape = (
  cappedSeries: ReadonlyArray<RawBarChartSeriesInput | null | undefined>,
  categoryCount: number
): string | null => {
  for (let i = 0; i < cappedSeries.length; i++) {
    const seriesItem = cappedSeries[i];

    if (!seriesItem || !Array.isArray(seriesItem.values)) {
      return `series[${i}].values is missing or not an array`;
    }

    if (seriesItem.values.length !== categoryCount) {
      return `series[${i}].values length (${seriesItem.values.length}) does not match categoryAxis.categories length (${categoryCount})`;
    }
  }

  return null;
};

const normalizeLabelFormat = (format: unknown): ChartLabelFormat => {
  if (format === undefined || format === null) {
    return DEFAULT_LABEL_FORMAT;
  }

  if (isChartLabelFormat(format)) {
    return format;
  }

  warn(
    `Unknown label format ${safeStringify(
      format
    )}, using "${DEFAULT_LABEL_FORMAT}"`
  );

  return DEFAULT_LABEL_FORMAT;
};

const normalizeSeriesEntries = (
  cappedSeries: ReadonlyArray<RawBarChartSeriesInput | null | undefined>
): BarChartSeries[] => {
  let hasCoercedValues = false;
  let hasDefaultedLabels = false;

  const normalized = cappedSeries.map(
    (singleSeries, seriesIndex): BarChartSeries => {
      const safeSeries = singleSeries as RawBarChartSeriesInput;
      const rawValues = safeSeries.values as ReadonlyArray<unknown>;

      let label: string;

      if (isNonEmptyString(safeSeries.label)) {
        label = safeSeries.label;
      } else {
        label = `Series ${seriesIndex + 1}`;
        hasDefaultedLabels = true;
      }

      const values = rawValues.map((rawValue): number | null => {
        if (rawValue === null) return null;
        if (isFiniteNumber(rawValue)) return rawValue;
        hasCoercedValues = true;

        return null;
      });

      return { label, values };
    }
  );

  if (hasCoercedValues) {
    warn('Some series values were not finite numbers; coerced to null');
  }
  if (hasDefaultedLabels) {
    warn('Some series labels were missing or empty; using fallback "Series N"');
  }

  return normalized;
};

/** Validates and normalizes untrusted bar chart input into a render-ready shape. */
export const validateBarChartProps = (
  input: BarChartValidationInput
): BarChartValidationResult => {
  const { categoryAxis, series, format } = input;

  const categoryAxisFailure = validateCategoryAxisShape(categoryAxis);

  if (categoryAxisFailure) {
    return { valid: false, reason: categoryAxisFailure };
  }

  if (!Array.isArray(series) || series.length === 0) {
    return { valid: false, reason: 'series is missing or empty' };
  }

  let cappedSeries: ReadonlyArray<RawBarChartSeriesInput | null | undefined> =
    series;

  if (series.length > CHART_MAX_SERIES) {
    warn(
      `series.length (${series.length}) exceeds maximum of ${CHART_MAX_SERIES}; using first ${CHART_MAX_SERIES} series`
    );
    cappedSeries = series.slice(0, CHART_MAX_SERIES);
  }

  const safeCategoryAxis = categoryAxis as RawBarChartCategoryAxisInput;
  const categoryCount = (safeCategoryAxis.categories as readonly string[])
    .length;
  const seriesFailure = validateSeriesShape(cappedSeries, categoryCount);

  if (seriesFailure) {
    return { valid: false, reason: seriesFailure };
  }

  const normalizedFormat = normalizeLabelFormat(format);
  const normalizedSeries = normalizeSeriesEntries(cappedSeries);

  const hasRenderableValue = normalizedSeries.some((singleSeries) =>
    singleSeries.values.some((value) => value !== null)
  );

  if (!hasRenderableValue) {
    return {
      valid: false,
      reason: 'series contain no renderable (finite) values',
    };
  }

  const normalizedCategoryAxis: BarChartCategoryAxis = {
    label:
      typeof safeCategoryAxis.label === 'string' ? safeCategoryAxis.label : '',
    categories: [...(safeCategoryAxis.categories as readonly string[])],
  };

  return {
    valid: true,
    normalized: {
      categoryAxis: normalizedCategoryAxis,
      series: normalizedSeries,
      format: normalizedFormat,
    },
  };
};
